 
import React from 'react';
import { formatNumber } from '@cars4ever/public-ui';

import {
  FuelType,
  GearType,
  CommonStep,
  CommonAction,
  EnergyClass,
  BodyType,
  CarType,
  PaintType,
} from '@digital-dealer/enums';

import TBasicTexts from '../types/TBasicTexts';

const basicTexts: TBasicTexts = {
  common: {
    moneyUnits: {
      default: 'kr.',
      businessType: 'kr.',
    },
    fuelTypes: {
      [FuelType.Diesel]: 'Diesel',
      [FuelType.Petrol]: 'Bensin',
      [FuelType.Hybrid]: 'Plug-In Hybrid',
      [FuelType.Electric]: 'El',
      [FuelType.MildHybrid]: 'Hybrid',
      [FuelType.EREV]: 'EREV',
    },
    gearTypes: {
      [GearType.Automatic]: 'Automat',
      [GearType.Manual]: 'Manuell',
    },
    paintType: {
      [PaintType.Standard]: 'Standardlack',
      [PaintType.Metallic]: 'Metallic-lack',
      [PaintType.Special]: 'Specialfärg',
      [PaintType.Pastel]: 'Pastel',
      [PaintType.Unilack]: 'Unilack',
    },
    bodyTypes: {
      [BodyType.Cabriolet]: 'Cabriolet',
      [BodyType.Coupe]: 'Kupé',
      [BodyType.Hatchback]: 'Halvkombi',
      [BodyType.MPV]: 'Familjebilar',
      [BodyType.Pickup]: 'PickUp',
      [BodyType.Sedan]: 'Sedan',
      [BodyType.StationCar]: 'Kombi',
      [BodyType.SUV]: 'SUV',
      [BodyType.Van]: 'Transportbil',
    },
    submit: 'Skicka',
    change: 'Ändra',
    proceed: 'Fortsätt',
    close: 'Tillbaka',
    moreInfo: 'Mer info',
    showMore: 'Visa fler',
    showAll: 'Visa alla',
    add: 'Välj',
    remove: 'Ta bort',
    noneSelected: 'Ingen vald',
    selected: 'Vald',
    standard: 'Standard',
    gear: 'Växellåda',
    fuelType: 'Bränsle',
    horsePower: 'Hästkrafter',
    consumption: 'Bränsleförbrukning',
    drivingRange: 'Räckvidd',
    cargoVolume: 'Bagagevolym',
    highlighted: 'Höjdpunkter',
    accessories: 'Tillbehör',
    vanEquipment: 'Indretning',
    emission: (
      <>
        CO<sub>2</sub> -utsläpp
      </>
    ),
    volume: 'Bagagevolym',
    batteryCapacity: 'Batteri',
    units: {
      consumption: 'l/100km',
      emission: 'g/km',
      km: 'km',
      squareMeters: 'm3',
      millimeters: 'mm',
      year: 'år',
      percent: '%',
      kg: 'kg',
      hp: 'hk',
      batteryCapacity: 'kWh',
    },
    energyClasses: {
      [EnergyClass.A3Plus]: 'A+++',
      [EnergyClass.A2Plus]: 'A++',
      [EnergyClass.APlus]: 'A+',
      [EnergyClass.A]: 'A',
      [EnergyClass.B]: 'B',
      [EnergyClass.C]: 'C',
      [EnergyClass.D]: 'D',
      [EnergyClass.E]: 'E',
      [EnergyClass.F]: 'F',
      [EnergyClass.G]: 'G',
    },
    apply: 'Ansök',
    reset: 'Nollställ',
    color: 'Färg',
    interior: 'Interiör',
    error: {
      serverDown: 'Servern ligger nere',
      tryReload: 'Testa att ladda om sidan eller kom tillbaka senare',
    },
  },
  fields: {
    labels: {
      firstName: 'Förnamn',
      lastName: 'Efternamn',
      phoneNumber: 'Telefon',
      message: 'Information till återförsäljaren',
      email: 'E-mail',
      cvr: 'Organisationsnummer',
      companyName: 'Företagsnamn',
      zip: 'Postnummer',
      city: 'Stad',
      address: 'Adress',
      tradeIn: {
        isRequested: {
          [CommonAction.TestDriveBooking]: 'Betygsätt min begagnade bil',
          [CommonAction.OfferRequest]:
            'Min begagnade bil måste ingå i erbjudandet',
        },
        plateNumber: 'Registreringsnummer',
        odometer: 'Antal körda kilometer',
      },
      cpr: 'CPR',
    },
    errors: {
      required: 'Nödvändigt',
      phoneNumber: 'Felaktigt telefonnummer',
      email: 'Felaktig e-postadress',
      cvr: 'Felaktigt organisationsnummer',
      zip: 'Felaktigt postnummer',
      tradeIn: {
        plateNumber: 'Fel registreringsnummer',
      },
      maxChars: (max: number): string => `Max antal tecken ${max}`,
      cpr: 'Felaktigt cpr',
      min: (value: number): string =>
        `Det måste vara mer än eller lika med ${formatNumber(value)}`,
      max: (value: number): string =>
        `Det måste vara mindre än eller lika med ${formatNumber(value)}`,
    },
  },
  modules: {
    modelChooser: {
      title: 'Välj din bil',
      wrongFilters: {
        title: 'Tyvärr',
        subTitle: 'Dina filter gav inte något resultat',
        button: 'Visa alla modeller',
      },
      filters: {
        title: 'Filter',
        fuelType: 'Bränsletyp',
        bodyType: 'Biltyp',
        brand: 'Märke',
        price: 'Pris',
        min: 'min',
        max: 'max',
      },
      priceFrom: {
        [CarType.PersonalCar]: 'Välj pris från',
        [CarType.Van]: 'Välj pris från',
      },
      placeholder: {
        title: 'Tyvärr finns det ingen tillgänglig modell',
      },
    },
    modelViewer: {
      soldOut: {
        title: 'Slutsåld',
        modelSoldOut: 'Tyvärr är denna modell just nu slutsåld',
        tryAnotherModel: 'Försök gärna med en annan modell',
        backToModels: 'Tilbaka till alla modeller',
      },
      notAvailable: {
        title: 'Möjlighet inte längre tillgänglig',
        text:
          'Tyvärr är denna bil inte längre tillgänglig. Men du kan bygga din egen bil.',
        button: 'Bygg din bil',
      },
      overview: {
        back: 'Välj bilmodell',
        disclaimer: {
          [CarType.PersonalCar]:
            'Bilen på bilden kan vara extrautrustad. Vi reserverar oss för felskrivningar, pris- och produktändringar. Erbjudandet gäller så långt lagret räcker.',
          [CarType.Van]:
            'Bilen på bilden kan vara extrautrustad. Vi reserverar oss för felskrivningar, pris- och produktändringar. Erbjudandet gäller så långt lagret räcker.',
        },
      },
      gallery: {
        video: {
          button: 'Kolla på video',
          title: 'Videopresentation',
        },
        thumbnails: {
          showMore: 'Visa mer',
          showLess: 'Visa mindre',
        },
      },
      configuration: {
        steps: {
          [CommonStep.Dimensions]: {
            title: 'Dimensioner',
            expandedTitle: 'Välj dimensioner',
            volume: 'Bagagevolym',
            length: 'Längd',
          },
          [CommonStep.Trims]: {
            title: 'Utrustningsnivå',
            expandedTitle: 'Välj utrustningsnivå',
            comparisonButtonTitle: 'Jämför',
            compareEquipment: {
              title: 'Jämför utrustningsnivå',
              backToConfigurator: 'Tilbaka till konfiguratorn',
              equipment: {
                none: 'Inte tillgängligt',
                optional: 'Tillval',
                included: 'Inkluderat',
              },
            },
            trimsGroup: {
              availableSubTrimsLabel: 'extra utrustningspaket',
            },
          },
          [CommonStep.EnginesGears]: {
            title: 'Motor',
            expandedTitle: 'Välj motor',
          },
          [CommonStep.Colors]: {
            expandedTitle: 'Välj färg',
            roofColor: {
              title: 'Takfärg',
            },
            colorPack: {
              title: 'Färgpaket',
            },
          },
          [CommonStep.Interiors]: {
            expandedTitle: 'Välj interiör',
          },
          [CommonStep.Accessories]: {
            title: 'Tillbehör',
            expandedTitle: 'Välj tillbehör',
            cartTitle: 'Välj tillbehör',
          },
          [CommonStep.ExtraEquipment]: {
            title: 'Tillval',
            expandedTitle: 'Välj tillval ',
            cartTitle: 'Välj tillval ',
            lockLabel: 'Begränsad',
            conflictModal: {
              title: 'Tillvalskonflikt',
              subtitle: 'Välj bara ett.',
              description:
                'Denna utrustning är inte kompatibel med det redan valda.',
              continueButton: 'Fortsätt',
            },
          },
          [CommonStep.Weights]: {
            title: 'Vikt',
            expandedTitle: 'Välj vikt',
            label: 'Totalvikt',
            description:
              'Totalvikt = högsta tillåtna fordonsvikt med drivmedel, förare, passagerare och last.',
          },
          [CommonStep.VanEquipment]: {
            title: 'Indretning',
            expandedTitle: 'Välj indretning',
            cartTitle: 'Välj indretning',
          },
          [CommonStep.ChargingSubscriptions]: {
            title: 'Laddningsabonnemang',
            subscriptionPriceLabel: 'Abonnemang',
            installationPriceLabel: 'Installation',
            expandedTitle: 'Välja Laddningsabonnemang',
            noCharge: 'Intet Laddningsabonnemang',
            unitsPerMonth: 'kr/mån',
          },
        },
      },
      checkout: {
        title: {
          [CarType.PersonalCar]: 'Din bil',
          [CarType.Van]: 'Din bil',
        },
        total: 'Total',
        technicalSpecs: {
          emission: (
            <>
              CO<sub>2</sub> -utsläpp
            </>
          ),
          ownerTax: 'Vägskatt',
        },
        color: 'Färg',
        interior: 'Interiör',
        standardEquipmentTitle: 'Standardutrustning',
        extraEquipmentTitle: 'Extrautrustning och tillval',
        van: {
          long: 'Längd',
        },
        actions: {
          consent: {
            text: (
              actionButton: string,
              privacyPolicyLink: string,
              clientPrivacyPolicyLink: string,
            ): string =>
              `Genom att klicka på "${actionButton}" samtycker jag till behandling av mina personuppgifter i enlighet med följande ${privacyPolicyLink}
             ${
  clientPrivacyPolicyLink
    ? `& återförsäljare ${clientPrivacyPolicyLink}`
    : ''
}`,
            linkText: 'personuppgiftspolicy',
          },
          [CommonAction.TestDriveBooking]: {
            actionButtonTitle: 'Boka provkörning',
            title: 'Boka provkörning',
            successPage: {
              title: 'Tack',
              description: 'Du kommer att bli kontaktad',
            },
          },
          [CommonAction.OfferRequest]: {
            actionButtonTitle: 'Få ett erbjudande',
            title: 'Få ett erbjudande',
            successPage: {
              title: 'Tack',
              description: 'Du kommer att bli kontaktad',
            },
          },
          [CommonAction.ShareLink]: {
            actionButtonTitle: 'Spara din bil',
            title: 'Skriv in din e-postadress',
            successPage: {
              description:
                'Du kommer snart att få ett mejl med en länk till din bil',
            },
          },
        },
        mailingSubscriptionForm: {
          submit: 'Prenumerera och stäng',
          consent: {
            text: (brand: string): string =>
              `Jag vill få e-post från ${brand} Sverige med marknadsföring av ${brand} produkter, information, erbjudanden och nyheter relaterade till ${brand}. Läs mer om hur vi behandlar din information i vår`,
            linkText: 'personuppgiftspolicy',
          },
        },
        storePicker: {
          title: 'Välj återförsäljare',
          loadError:
            'Tyvärr uppstod ett fel vid inläsning av återförsäljarna. Testa att uppdatera sidan eller gå vidare, så kontaktar vi dig.',
        },
        errorPage: {
          title: 'Fel',
          description: 'Tyvärr, servern svarar inte',
          button: 'Uppdatera formuläret',
        },
        hints: {
          [CommonAction.TestDriveBooking]: [
            'Fyll i formuläret för att bli kontaktad om provkörning.',
            'Våra kundrådgivare tar emot din förfrågan och förbereder provkörning enligt dina önskemål.',
            'Vår kundrådgivare kontaktar dig och bokar in provkörning hos valfri återförsäljare.',
          ],
          [CommonAction.OfferRequest]: [
            'Fyll i formuläret för att bli kontaktad med en offert.',
            'Din valda återförsäljare förbereder en offert enligt dina önskemål.',
            'Återförsäljaren kontaktar dig med ett skräddarsytt erbjudande. Samtalet är förbehållslöst.',
          ],
        },
      },
    },
  },
};

export default basicTexts;
